import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  MapPin,
  CheckCircle,
  Loader,
  AlertTriangle,
  ClipboardCheck,
  Mail,
  IdCard,
  ShieldCheck,
} from "lucide-react";
import { toast } from "react-toastify";
import SignaturePad from "../../components/UI/SignaturePad";
import { pohAPI } from "../../services/api";
import { FileDropzone, GeoStatusBanner, useGeoCapture } from "../../components/poh/PohEvidence";
import OtpCheckEmailCard from "../../components/poh/OtpCheckEmailCard";
import POHChainCard, { type POHStepData } from "../../components/poh/POHChainCard";
import POHCargoDetails from "../../components/poh/POHCargoDetails";
import type { PreviousSignature } from "../../components/poh/POHPreviousSignatures";

type PageState =
  | "loading"
  | "identity"
  | "otp-sending"
  | "otp"
  | "otp-verifying"
  | "sign"
  | "submitting"
  | "done"
  | "error";

const DriverDeliveryPOHSignPage: React.FC = () => {
  const { qrToken } = useParams<{ qrToken: string }>();
  const [state, setState] = useState<PageState>("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const [shipment, setShipment] = useState<any>(null);
  const [previousSignatures, setPreviousSignatures] = useState<PreviousSignature[]>([]);

  const [signerName, setSignerName] = useState("");
  const [driverEmail, setDriverEmail] = useState("");
  const [identityNumber, setIdentityNumber] = useState("");
  const [resendingOtp, setResendingOtp] = useState(false);

  const [signatureData, setSignatureData] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const geo = useGeoCapture(true);

  useEffect(() => {
    if (!qrToken) { setErrorMsg("Invalid QR code link."); setState("error"); return; }
    pohAPI.getDeliveryDriverSignInfo(qrToken)
      .then((res) => {
        const { shipment: s, previousSignatures: ps, emailVerifiedFor } = res.data.data;
        setShipment(s);
        setPreviousSignatures(ps ?? []);
        if (emailVerifiedFor) {
          setDriverEmail(emailVerifiedFor);
          setState("sign");
        } else {
          setState("identity");
        }
      })
      .catch((err) => {
        setErrorMsg(err?.response?.data?.message || "Invalid or expired QR code.");
        setState("error");
      });
  }, [qrToken]);

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signerName.trim()) return toast.error("Please enter your full name");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(driverEmail.trim())) return toast.error("Enter a valid email");
    if (identityNumber.trim().length < 4) return toast.error("Enter your ID / passport number (min 4 chars)");
    try {
      setState("otp-sending");
      const res = await pohAPI.requestDeliveryDriverOtp(qrToken!, {
        signerName: signerName.trim(),
        driverEmail: driverEmail.trim(),
        identityNumber: identityNumber.trim(),
      });
      void res;
      setState("otp");
      toast.info("Verification code sent to your email.");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to send code");
      setState("identity");
    }
  };

  const handleVerifyOtp = async (otpValue: string): Promise<void> => {
    if (otpValue.trim().length !== 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    try {
      setState("otp-verifying");
      await pohAPI.verifyDeliveryDriverOtp(qrToken!, otpValue.trim());
      toast.success("Email verified");
      setState("sign");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Invalid code");
      setState("otp");
    }
  };

  const handleResendOtp = async () => {
    if (!signerName || !driverEmail || !identityNumber) return;
    try {
      setResendingOtp(true);
      await pohAPI.requestDeliveryDriverOtp(qrToken!, {
        signerName: signerName.trim(),
        driverEmail: driverEmail.trim(),
        identityNumber: identityNumber.trim(),
      });
      toast.info("A new code has been sent.");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to resend code");
    } finally {
      setResendingOtp(false);
    }
  };

  const handleSign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signatureData) return toast.error("Please provide your signature");
    if (geo.state.status !== "ok") return toast.error("Location is required. Allow location access and retry.");
    try {
      setState("submitting");
      await pohAPI.driverDeliveryQrSign(qrToken!, { signatureData }, geo.state.geo, files);
      setState("done");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to submit signature");
      setState("sign");
    }
  };

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center space-y-3">
          <AlertTriangle className="h-10 w-10 text-red-500 mx-auto" />
          <h2 className="text-lg font-semibold text-gray-900">Cannot Load Handover</h2>
          <p className="text-sm text-gray-500">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (state === "done") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center space-y-4">
          <CheckCircle className="h-14 w-14 text-green-500 mx-auto" />
          <h2 className="text-xl font-bold text-gray-900">Delivery Confirmed!</h2>
          <p className="text-sm text-gray-600">
            You have signed as the delivery driver for shipment{" "}
            <span className="font-semibold">{shipment?.shipmentNumber}</span>.
            Both parties have now signed — the shipment is marked as Completed and locked.
          </p>
          <div className="bg-green-50 rounded-lg px-4 py-3 text-sm text-green-700 font-medium">
            Signed at: {new Date().toLocaleString()}
          </div>
        </div>
      </div>
    );
  }

  const pickup = shipment?.pickup;
  const delivery = shipment?.delivery;

  const chainCard = (() => {
    const consignerSig    = previousSignatures.find((s) => s.role === "Consignor");
    const pickupDriverSig = previousSignatures.find((s) => s.role === "Pickup Driver");
    const recipientSig    = previousSignatures.find((s) => s.role === "Recipient");
    const allPickupConfirmed = !!(consignerSig && pickupDriverSig);
    const pickupStep: POHStepData = {
      phase: "pickup",
      status: allPickupConfirmed ? "confirmed" : "pending",
      slots: [
        consignerSig
          ? { ...consignerSig,    role: "Consignor",     status: "confirmed" as const }
          : { role: "Consignor",     status: "pending"   as const },
        pickupDriverSig
          ? { ...pickupDriverSig, role: "Pickup Driver", status: "confirmed" as const }
          : { role: "Pickup Driver", status: "pending"   as const },
      ],
    };
    const deliveryStep: POHStepData = {
      phase: "delivery",
      status: "pending",
      slots: [
        recipientSig
          ? { ...recipientSig, role: "Recipient",       status: "confirmed" as const }
          : { role: "Recipient",       status: "pending" as const },
        { role: "Delivery Driver", status: "pending" as const },
      ],
    };
    return <POHChainCard pickup={pickupStep} delivery={deliveryStep} />;
  })();

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <ClipboardCheck className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Proof of Delivery</h1>
              <p className="text-sm text-gray-500 font-mono">{shipment?.shipmentNumber}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-1 mb-1">
                <MapPin className="h-3.5 w-3.5 text-green-600" />
                <span className="text-xs font-semibold text-gray-500 uppercase">From</span>
              </div>
              <p className="text-sm font-medium text-gray-800">{pickup?.companyName || "—"}</p>
              <p className="text-xs text-gray-500">
                {pickup?.address?.city}{pickup?.address?.city && ", "}{pickup?.address?.country?.name || ""}
              </p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 justify-end mb-1">
                <span className="text-xs font-semibold text-gray-500 uppercase">To</span>
                <MapPin className="h-3.5 w-3.5 text-red-600" />
              </div>
              <p className="text-sm font-medium text-gray-800">{delivery?.companyName || "—"}</p>
              <p className="text-xs text-gray-500">
                {delivery?.address?.city}{delivery?.address?.city && ", "}{delivery?.address?.country?.name || ""}
              </p>
            </div>
          </div>
        </div>

        <POHCargoDetails cargoItems={shipment?.cargoItems} referenceNumber={shipment?.referenceNumber} />

        {chainCard}

        <GeoStatusBanner state={geo.state} onRetry={geo.capture} />

        {(state === "identity" || state === "otp-sending") && (
          <form onSubmit={handleRequestOtp} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <IdCard className="h-4 w-4 text-blue-600" />
              Step 1: Verify your identity
            </h2>
            <p className="text-sm text-gray-500">We send a one-time code to your email to confirm it's really you.</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
              <input value={signerName} onChange={(e) => setSignerName(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
              <input type="email" value={driverEmail} onChange={(e) => setDriverEmail(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ID / Passport Number <span className="text-red-500">*</span></label>
              <input value={identityNumber} onChange={(e) => setIdentityNumber(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <button type="submit" disabled={state === "otp-sending"} className="w-full py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {state === "otp-sending" && <Loader className="h-4 w-4 animate-spin" />}
              <Mail className="h-4 w-4" /> Send verification code
            </button>
          </form>
        )}

        {(state === "otp" || state === "otp-verifying") && (
          <OtpCheckEmailCard
            email={driverEmail}
            onSubmit={handleVerifyOtp}
            onResend={handleResendOtp}
            onChangeEmail={() => setState("identity")}
            submitting={state === "otp-verifying"}
            resending={resendingOtp}
          />
        )}

        {(state === "sign" || state === "submitting") && (
          <form onSubmit={handleSign} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs text-green-700">
              <ShieldCheck className="h-4 w-4" />
              Email verified — you can sign below
            </div>
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4 text-blue-600" />
              Step 3: Sign as Delivered
            </h2>
            <p className="text-sm text-gray-500">
              By signing below you confirm delivery to the recipient. Once you sign, the shipment is marked Completed and locked.
            </p>
            <SignaturePad label="Driver Signature" onSave={setSignatureData} width={600} height={200} />
            <FileDropzone files={files} onChange={setFiles} disabled={state === "submitting"} />
            <button type="submit" disabled={state === "submitting" || geo.state.status !== "ok"} className="w-full py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {state === "submitting" && <Loader className="h-4 w-4 animate-spin" />}
              Confirm Delivery & Sign
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default DriverDeliveryPOHSignPage;
